import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Zap, Menu, X, LogOut, Globe } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";

export default function Header() {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  const links = [
    { href: "/", label: t("nav_home"), key: "home" },
    { href: "/#models", label: t("nav_models"), key: "models" },
    { href: "/#how", label: t("nav_how"), key: "how" },
    { href: "/#leaderboard", label: t("nav_leaderboard"), key: "leaderboard" },
  ];

  const LangToggle = () => (
    <button
      onClick={() => setLang(lang === "en" ? "kn" : "en")}
      className="flex items-center gap-1.5 px-3 h-9 rounded-full border border-white/15 text-xs uppercase tracking-[0.15em] text-white/70 hover:text-[#00FF66] hover:border-[#00FF66]/50 transition-colors"
      data-testid="lang-toggle"
      aria-label="Toggle language"
    >
      <Globe className="w-3.5 h-3.5" />
      <span className="font-bold">{lang === "en" ? "ಕನ್ನಡ" : "EN"}</span>
    </button>
  );

  return (
    <header
      className="sticky top-0 z-50 bg-black/70 backdrop-blur-xl border-b border-white/10"
      data-testid="app-header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="brand-logo">
          <span className="w-9 h-9 rounded-lg bg-[#00FF66] flex items-center justify-center shadow-[0_0_24px_#00FF6640]">
            <Zap className="w-5 h-5 text-black" strokeWidth={2.5} />
          </span>
          <div className="leading-tight">
            <div className="font-display font-black text-white text-lg tracking-tight">SHRI SHANTESHWAR</div>
            <div className="text-[10px] tracking-[0.3em] text-[#00FF66] uppercase">Enterprises · EV</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm text-white/70 hover:text-[#00FF66] transition-colors duration-200"
              data-testid={`nav-${l.key}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <LangToggle />
          {user ? (
            <>
              <Button
                variant="ghost"
                onClick={() => nav(user.role === "admin" ? "/admin" : "/dashboard")}
                className="text-white hover:text-[#00FF66] hover:bg-white/5"
                data-testid="header-dashboard-btn"
              >
                {user.role === "admin" ? "Admin" : "Dashboard"}
              </Button>
              <Button
                onClick={() => {
                  logout();
                  nav("/");
                }}
                variant="outline"
                className="border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white"
                data-testid="header-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" /> {t("nav_logout")}
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="header-login-link">
                <Button variant="ghost" className="text-white hover:text-[#00FF66] hover:bg-white/5">
                  {t("nav_login")}
                </Button>
              </Link>
              <Link to="/register" data-testid="header-signup-link">
                <Button className="rounded-full bg-[#00FF66] text-black hover:bg-[#00E55C] font-semibold px-6">
                  {t("nav_signup")}
                </Button>
              </Link>
            </>
          )}
        </div>

        <button
          className="md:hidden text-white"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/10 bg-black px-6 py-4 flex flex-col gap-4">
          {links.map((l) => (
            <a key={l.href} href={l.href} className="text-white/80" onClick={() => setOpen(false)}>
              {l.label}
            </a>
          ))}
          <div className="pt-2"><LangToggle /></div>
          {user ? (
            <>
              <Link to={user.role === "admin" ? "/admin" : "/dashboard"} className="text-[#00FF66]">
                {user.role === "admin" ? "Admin" : "Dashboard"}
              </Link>
              <button onClick={logout} className="text-left text-white/80">
                {t("nav_logout")}
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-white">{t("nav_login")}</Link>
              <Link to="/register" className="text-[#00FF66] font-semibold">{t("nav_signup")}</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}
